import { useState, useEffect } from 'react';
import { addSnare, addCarcass } from '../../lib/db';
import { syncService } from '../../lib/sync';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { getMyGroups, type GroupMembership } from '../../lib/api/groups';
import { useAuth } from '../../contexts/AuthContext';
import { generateRecordId } from '../../lib/recordId';

interface SnareFormData {
  groupId: string | null;
  latitude: number | null;
  longitude: number | null;
  description: string;
  material: 'wire' | 'cable' | 'nylon' | 'rope' | 'other';
  species: string;
  hadSnare: boolean;
  photos: string[];
}

interface SnareFormProps {
  userId: string;
  onSuccess?: () => void;
}

export function SnareForm({ userId, onSuccess }: SnareFormProps) {
  const { user } = useAuth();
  const isOnline = useOnlineStatus();
  const [entryType, setEntryType] = useState<'snare' | 'carcass'>('snare');
  const [showDescription, setShowDescription] = useState(false);
  const [approvedGroups, setApprovedGroups] = useState<GroupMembership[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [formData, setFormData] = useState<SnareFormData>({
    groupId: null,
    latitude: null,
    longitude: null,
    description: '',
    material: 'wire',
    species: '',
    hadSnare: false,
    photos: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isCapturingLocation, setIsCapturingLocation] = useState(false);

  useEffect(() => {
    async function loadApprovedGroups() {
      try {
        const groups = await getMyGroups();
        const approved = groups.filter(g => g.status === 'approved');
        setApprovedGroups(approved);
        // Auto-select first group if only one approved group
        if (approved.length === 1) {
          setFormData(prev => ({ ...prev, groupId: approved[0].id }));
        }
      } catch (err) {
        console.error('Failed to load groups:', err);
      } finally {
        setLoadingGroups(false);
      }
    }
    loadApprovedGroups();
  }, []);

  const captureLocation = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      return;
    }

    setIsCapturingLocation(true);
    setError(null);

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });

      if (permission.state === 'denied') {
        setError('Location denied. Enable in Settings.');
        setIsCapturingLocation(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }));
          setIsCapturingLocation(false);
        },
        (error) => {
          setError(error.code === error.PERMISSION_DENIED ? 'Location denied' : 'Location failed');
          setIsCapturingLocation(false);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    } catch (err) {
      setError('Location blocked. Use HTTPS.');
      setIsCapturingLocation(false);
    }
  };

  const handlePhotoCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const readers = Array.from(files).map(file => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers)
      .then(base64Images => {
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, ...base64Images],
        }));
      })
      .catch(() => setError('Photo failed'));
  };

  const removePhoto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.groupId) {
      setError('Please select a group');
      return;
    }

    if (formData.latitude === null || formData.longitude === null) {
      setError('Capture GPS location first');
      return;
    }

    setIsSubmitting(true);

    try {
      const timestamp = Date.now();
      const id = `${timestamp}-${Math.random().toString(36).substr(2, 9)}`;

      // Find the selected group to get group info
      const selectedGroup = approvedGroups.find(g => g.id === formData.groupId);
      if (!selectedGroup || !user) {
        setError('Please select a group');
        return;
      }

      // Generate record ID
      const recordId = await generateRecordId(user, selectedGroup, entryType);

      if (entryType === 'snare') {
        const snare = {
          id,
          snareNumber: recordId,
          groupId: formData.groupId,
          latitude: formData.latitude,
          longitude: formData.longitude,
          description: formData.description.trim(),
          material: formData.material,
          photos: formData.photos,
          userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          syncStatus: 'pending' as const,
        };
        await addSnare(snare);
      } else {
        // Carcass
        const carcass = {
          id,
          carcassNumber: recordId,
          groupId: formData.groupId,
          latitude: formData.latitude,
          longitude: formData.longitude,
          species: formData.species.trim(),
          hadSnare: formData.hadSnare,
          description: formData.description.trim(),
          photos: formData.photos,
          userId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          syncStatus: 'pending' as const,
        };
        await addCarcass(carcass);
      }

      if (isOnline) {
        const result = await syncService.syncAll();
        setSuccess(result.success ? 'Saved & synced!' : 'Saved offline');
      } else {
        setSuccess('Saved offline');
      }

      setFormData({
        groupId: formData.groupId,
        latitude: null,
        longitude: null,
        description: '',
        material: 'wire',
        species: '',
        hadSnare: false,
        photos: [],
      });
      setShowDescription(false);

      onSuccess?.();
    } catch (err) {
      setError('Save failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Toggle */}
      <div className="flex gap-3 px-4 pt-5 pb-4 border-b border-border bg-background">
        <button
          type="button"
          onClick={() => setEntryType('snare')}
          className={`flex-1 px-4 py-3 rounded-lg font-semibold text-base ${
            entryType === 'snare'
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary text-secondary-foreground'
          }`}
        >
          Snare
        </button>
        <button
          type="button"
          onClick={() => setEntryType('carcass')}
          className={`flex-1 px-4 py-3 rounded-lg font-semibold text-base ${
            entryType === 'carcass'
              ? 'bg-primary text-primary-foreground'
              : 'bg-secondary text-secondary-foreground'
          }`}
        >
          Carcass
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 relative">
        {/* Scrollable Content - with bottom padding to clear fixed button + nav */}
        <div className="flex-1 overflow-y-auto px-4 py-4 pb-56">
          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive rounded-lg text-destructive text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500 rounded-lg text-green-500 text-sm">
              {success}
            </div>
          )}

          {loadingGroups && (
            <div className="mb-4 p-3 bg-muted rounded-lg text-sm">
              Loading groups...
            </div>
          )}

          {!loadingGroups && approvedGroups.length === 0 && (
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500 rounded-lg text-yellow-600 text-sm">
              You need to join and be approved in a group before you can log {entryType === 'snare' ? 'snares' : 'carcasses'}.
              Go to the Groups tab to join a group.
            </div>
          )}

          <div className="space-y-5">
            {/* Group Selection */}
            {!loadingGroups && approvedGroups.length > 0 && (
              <div>
                <label htmlFor="group" className="block text-sm font-semibold mb-2 text-foreground">
                  Group
                </label>
                <select
                  id="group"
                  value={formData.groupId || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, groupId: e.target.value }))}
                  className="w-full px-4 py-3 text-base bg-background border border-input rounded-lg"
                  required
                >
                  <option value="">Select a group</option>
                  {approvedGroups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* GPS */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-foreground">GPS Location</label>
              <button
                type="button"
                onClick={captureLocation}
                disabled={isCapturingLocation}
                className="w-full px-4 py-3 text-base font-medium bg-primary text-primary-foreground rounded-lg disabled:opacity-50 active:opacity-80"
              >
                {isCapturingLocation ? 'Getting Location...' : formData.latitude ? 'Recapture' : 'Capture Location'}
              </button>
              {formData.latitude !== null && formData.longitude !== null && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)}
                </p>
              )}
            </div>

            {/* Material - only show for snares */}
            {entryType === 'snare' && (
              <div>
                <label htmlFor="material" className="block text-sm font-semibold mb-2 text-foreground">
                  Material
                </label>
                <select
                  id="material"
                  value={formData.material}
                  onChange={(e) => setFormData(prev => ({ ...prev, material: e.target.value as any }))}
                  className="w-full px-4 py-3 text-base bg-background border border-input rounded-lg"
                  required
                >
                  <option value="wire">Wire</option>
                  <option value="cable">Cable</option>
                  <option value="nylon">Nylon</option>
                  <option value="rope">Rope</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}

            {/* Species - only show for carcasses */}
            {entryType === 'carcass' && (
              <div>
                <label htmlFor="species" className="block text-sm font-semibold mb-2 text-foreground">
                  Species
                </label>
                <input
                  type="text"
                  id="species"
                  value={formData.species}
                  onChange={(e) => setFormData(prev => ({ ...prev, species: e.target.value }))}
                  className="w-full px-4 py-3 text-base bg-background border border-input rounded-lg"
                  placeholder="e.g. Elephant, Giraffe, etc."
                  maxLength={80}
                />
              </div>
            )}

            {/* Had Snare - only show for carcasses */}
            {entryType === 'carcass' && (
              <div className="flex items-center gap-3 p-4 bg-secondary rounded-lg">
                <input
                  type="checkbox"
                  id="hadSnare"
                  checked={formData.hadSnare}
                  onChange={(e) => setFormData(prev => ({ ...prev, hadSnare: e.target.checked }))}
                  className="w-5 h-5 rounded border-input"
                />
                <label htmlFor="hadSnare" className="text-sm font-medium text-foreground">
                  Snare was involved
                </label>
              </div>
            )}

            {/* Photos */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-foreground">Photos</label>
              <label
                htmlFor="photos"
                className="block w-full px-4 py-3 text-base font-medium bg-primary text-primary-foreground rounded-lg text-center cursor-pointer active:opacity-80"
              >
                Take Photo
              </label>
              <input
                type="file"
                id="photos"
                accept="image/*"
                multiple
                capture="environment"
                onChange={handlePhotoCapture}
                className="hidden"
              />
              {formData.photos.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-3">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={photo}
                        alt={`${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-7 h-7 flex items-center justify-center font-bold shadow-lg"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              {!showDescription ? (
                <button
                  type="button"
                  onClick={() => setShowDescription(true)}
                  className="text-sm text-muted-foreground font-medium"
                >
                  + Add note
                </button>
              ) : (
                <div>
                  <label htmlFor="description" className="block text-sm font-semibold mb-2 text-muted-foreground">
                    Note
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 text-base bg-background border border-input rounded-lg"
                    rows={3}
                    placeholder="Optional details..."
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Submit Button - anchored above bottom nav */}
        <div className="fixed bottom-16 left-0 right-0 border-t border-border bg-white px-4 py-4 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-yellow-500'}`} />
              <span className="text-sm font-medium text-muted-foreground">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-xl font-bold text-lg disabled:opacity-50 active:opacity-80 shadow-sm"
          >
            {isSubmitting ? 'Saving...' : `Log ${entryType === 'snare' ? 'Snare' : 'Carcass'}`}
          </button>
        </div>
      </form>
    </div>
  );
}
